import React, { useState } from 'react'

import { enrollmentHelperClient } from '@api/enrollmentClient'
import { getXCLEARCorrelationInfo } from '@api/utils'
import LookUpForm from '@components/pages/shared/LookUpForm'
import { Enrollment } from '@clear-denver/tpc-openapi-typescript'
import KtnCard from '@components/pages/ktn-lookup/KtnCard'
import { DiscreteEvent, dispatchCustomEvent, KPI } from '@utils/customEvents'
import PageContent from '@components/layout/PageContent'
import { Box } from '@clear/design-system'
import VerificationCodeForm from '@components/VerificationCodeForm'
import {
  KTNDetails,
  OptAppstatusChallengeRequest,
  OptKtndetailsSessionRequest,
  OtpKtndetailsRequest,
} from '@clear-denver/tpc-openapi-typescript/build/v1/enrollment'
import { Modal } from '@components/shared/Modal'
import { PlanTypes, useEnrollmentContext } from '@contexts/EnrollmentContext'
import { useRouter } from 'next/router'
import SelectVerificationMethodForm from '@components/SelectVerificationMethodForm'

interface FormData extends Enrollment.BiographicsLookup {
  otpSessionId?: string
}

function KtnLookupPage() {
  const [responseData, setResponseData] = useState<KTNDetails | null>(null)
  const [showKtnCard, setShowKtnCard] = useState<boolean>(false)
  const [verificationFormData, setVerificationFormData] =
    useState<FormData | null>(null)
  const [showVerificationForm, setShowVerificationForm] =
    useState<boolean>(false)
  const [showVerificationMethodForm, setShowVerificationMethodForm] =
    useState<boolean>(false)
  const [showModal, setShowModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { setSelectedPlan } = useEnrollmentContext()
  const router = useRouter()

  const onSubmitByBiographic = async (
    formData: Enrollment.BiographicsLookup
  ) => {
    setIsLoading(true)
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const baseMetaData = {
      kpi: KPI.USER_INTERACTION,
      xCLEARCorrelationInfo,
    }
    const dob = new Date(formData.dob)
    dispatchCustomEvent(Enrollment.LogEntryLevelEnum.INFO, {
      payload: {
        message: 'KTN lookup by biographics has begun',
      },
      metadata: {
        ...baseMetaData,
        event: DiscreteEvent.KTN_LOOKUP_BY_BIOGRAPHICS_BEGIN,
      },
    })

    try {
      const response = await enrollmentHelperClient.updatedKtndetailsLookup({
        xCLEARCorrelationInfo,
        biographicsLookup: {
          ...formData,
          dob,
        },
      })

      if (!!response.otpSessionId && !response.needCommMethod) {
        setVerificationFormData({
          ...formData,
          otpSessionId: response.otpSessionId,
        })
        setShowVerificationForm(true)
      } else if (response.needCommMethod) {
        setVerificationFormData({
          ...formData,
        })
        setShowVerificationMethodForm(true)
      }
      setIsLoading(false)
    } catch (error) {
      setShowModal(true)
      setIsLoading(false)
    }
  }

  async function onVerificationMethodSubmit(formData) {
    setIsLoading(true)
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const requestBody: OtpKtndetailsRequest = {
      otpForm: {
        emailAddress: formData.email,
        phoneNumber: formData.countryCode + '.' + formData.phoneNumber,
      },
      xCLEARCorrelationInfo,
    }

    try {
      await enrollmentHelperClient
        .otpKtndetails(requestBody)
        .then((response) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setVerificationFormData((prevState: any) => ({
            ...prevState,
            otpSessionId: response.otpSessionId,
          }))
          setShowVerificationMethodForm(false)
          setShowVerificationForm(true)
        })
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      setShowModal(true)
    }
  }

  async function onVerificationSubmit(formData) {
    setIsLoading(true)
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const requestBody: OptAppstatusChallengeRequest = {
      otpSessionId: verificationFormData?.otpSessionId as never,
      otpCode: formData.verificationCode,
      xCLEARCorrelationInfo,
    }
    await enrollmentHelperClient
      .optAppstatusChallenge(requestBody)
      .then(async () => {
        const payload: OptKtndetailsSessionRequest = {
          otpSessionId: requestBody.otpSessionId,
          xCLEARCorrelationInfo,
          biographicsLookup: {
            ...verificationFormData,
            ...formData,
          },
        }
        try {
          const response = await enrollmentHelperClient.optKtndetailsSession(
            payload
          )
          if (response) {
            setShowVerificationForm(false)
            setShowKtnCard(true)
            setResponseData(response)
          }
          setIsLoading(false)
        } catch (error) {
          setIsLoading(false)
          setShowModal(true)
        }
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((e: any) => {
        setIsLoading(false)
        console.log(e)
        setShowModal(true)
      })
  }

  const onClickEnroll = () => {
    setSelectedPlan(PlanTypes.ENROLL)
    router.push('/enroll/document-check')
  }

  const renderComponents = () => {
    if (showVerificationForm) {
      return (
        <PageContent>
          <Box
            width={['95%', '572px']}
            marginLeft="auto"
            marginRight="auto"
            border="1px solid #CCCED1"
            borderRadius="8px"
            paddingX={['20px', '50px']}
            paddingY={['20px', '40px']}
            backgroundColor="neutralsWhite"
          >
            <VerificationCodeForm
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              formData={verificationFormData!}
              onSubmit={onVerificationSubmit}
              onSubmitContact={() =>
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                onSubmitByBiographic(verificationFormData!)
              }
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              verificationFormData={verificationFormData as any}
              isLoading={isLoading}
            />
          </Box>
        </PageContent>
      )
    }

    if (showVerificationMethodForm) {
      return (
        <SelectVerificationMethodForm
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          formData={verificationFormData!}
          onSubmitData={onVerificationMethodSubmit}
        />
      )
    }

    if (showKtnCard && responseData) {
      return <KtnCard data={responseData} />
    }

    return (
      <LookUpForm
        headTitle="What is my KTN? | TSA&nbsp;PreCheck® Known Traveler Number Look Up"
        headDescription="If you are a TSA&nbsp;PreCheck® member, you can look up your known traveler number online. Find your KTN here."
        title="Look Up KTN"
        onSubmitByBiographic={onSubmitByBiographic}
      />
    )
  }
  return (
    <>
      {renderComponents()}
      <Modal
        showModal={showModal}
        setShowModal={setShowModal}
        header="Information Not Found"
        body="We couldn’t find a record based on the information entered. If you have been issued a KTN and are attempting to renew, please confirm you’ve entered the correct information and try again. You can contact customer service at CLEARCustomerSupport@tsa.dhs.gov for additional assistance or begin a new enrollment."
        successButtonText="Enroll"
        canClose={true}
        successClick={onClickEnroll}
      />
    </>
  )
}

export default KtnLookupPage
